import { useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import { motion, useReducedMotion } from "framer-motion";
import { api } from "../api";
import { COCKPIT } from "../constants/testIds";
import { useAppData } from "../contexts/AppDataContext";
import { dateRangeFor, formatCurrency, timeAwareGreeting } from "../lib/format";
import { fadeUp } from "../lib/motion";
import { DrillDown } from "../components/common/DrillDown";
import { StatCard } from "../components/common/StatCard";
import { OccupancyRing } from "../components/common/OccupancyRing";
import { EmptyCockpitState } from "../components/common/EmptyCockpitState";
import { ErrorState, OfflineBanner } from "../components/common/ErrorState";
import { NeedsAttentionPanel } from "../components/cockpit/NeedsAttentionPanel";
import { RecentMembersTable } from "../components/cockpit/RecentMembersTable";
import { QuickActions } from "../components/cockpit/QuickActions";
import { useOnlineStatus } from "../hooks/useOnlineStatus";

const DRILLDOWN_LINKS = {
  "active-members": { label: "Open Members", to: "/app/members?filter=active" },
  "dues-outstanding": { label: "Open Members", to: "/app/members?filter=dues" },
  "at-risk": { label: "Open Members", to: "/app/members?filter=at_risk" },
  "ad-roi": { label: "Open Marketing", to: "/app/marketing" },
};

const RANGE_LABELS = { today: "Today", week: "Last 7 days", month: "Last 30 days" };

export default function OwnerCockpit() {
  const { me, entitlements, selectedBranchId } = useAppData();
  const navigate = useNavigate();
  const isOnline = useOnlineStatus();
  const reducedMotion = useReducedMotion();
  const [drilldown, setDrilldown] = useState(null);
  const [range, setRange] = useState("month");

  const branchParam = selectedBranchId === "all" ? undefined : selectedBranchId;

  const membersQ = useQuery({
    queryKey: ["members", "cockpit-page"],
    queryFn: async () => (await api.get("/members", { params: { limit: 200 } })).data.members,
  });

  // UX-RM read-models: true aggregations (not client-side counts capped by pagination) for
  // every KPI/attention figure the cockpit shows — see readmodels_engine.py.
  const summaryQ = useQuery({
    queryKey: ["cockpit-summary", branchParam],
    queryFn: async () => (await api.get("/reports/cockpit-summary", { params: { branch_id: branchParam } })).data,
  });

  const attentionQ = useQuery({
    queryKey: ["cockpit-attention", branchParam],
    queryFn: async () => (await api.get("/reports/attention", { params: { branch_id: branchParam } })).data,
  });

  // Revenue KPI respects the branch + date-range selectors (compute_pnl omits the branch filter
  // entirely when branch_id is undefined -> a true org-wide roll-up for "All branches"). This is
  // a distinct, period-selectable figure from cockpit-summary's fixed "collections_today"/"mrr",
  // so it stays on the pre-existing `/reports/pnl` endpoint.
  const pnlQ = useQuery({
    queryKey: ["pnl", selectedBranchId, range],
    queryFn: async () => (await api.get("/reports/pnl", {
      params: { branch_id: branchParam, ...dateRangeFor(range) },
    })).data,
    enabled: entitlements.expense !== false,
  });

  const recentMembers = useMemo(() => {
    if (!membersQ.data) return [];
    return [...membersQ.data].sort((a, b) => new Date(b.last_visit_at || 0) - new Date(a.last_visit_at || 0)).slice(0, 10);
  }, [membersQ.data]);

  // Return on ad spend, so the number an owner most wants is on the screen they land on.
  // Fails soft: a role without marketing access just doesn't get the tile.
  const marketingQ = useQuery({
    queryKey: ["cockpit-marketing-roi"],
    queryFn: async () => (await api.get("/marketing/attribution")).data,
    retry: false,
  });
  const adRoi = marketingQ.data?.totals;

  const balancesQ = useQuery({
    queryKey: ["recent-balances", recentMembers.map((m) => m.id).join(",")],
    queryFn: async () => {
      // allSettled: one member with no resolvable balance (e.g. a since-erased DSR test
      // fixture) must never blank out every other row's real dues figure.
      const results = await Promise.allSettled(recentMembers.map((m) => api.get(`/members/${m.id}/balance`)));
      return Object.fromEntries(
        results.filter((r) => r.status === "fulfilled").map((r) => [r.value.data.member_id, r.value.data.dues_balance]),
      );
    },
    enabled: recentMembers.length > 0,
  });

  const tableRows = recentMembers.map((m) => ({ ...m, dues: balancesQ.data?.[m.id] }));

  const isEmpty = membersQ.isSuccess && membersQ.data.length === 0;
  const isLoading = membersQ.isLoading || summaryQ.isLoading;
  const hasError = membersQ.isError || summaryQ.isError || attentionQ.isError;
  const lastUpdated = membersQ.dataUpdatedAt ? new Date(membersQ.dataUpdatedAt) : null;

  if (hasError) {
    return (
      <ErrorState
        error={membersQ.error || summaryQ.error || attentionQ.error}
        onRetry={() => { membersQ.refetch(); summaryQ.refetch(); attentionQ.refetch(); }}
      />
    );
  }

  const occupancy = summaryQ.data?.occupancy_now;
  // UX-01-PATCH §3 "thin-data churn: if churn_rate is null, render the at-risk count instead
  // (no misleading %)" — the endpoint returns both, this is purely a UI pick.
  const churnAvailable = summaryQ.data?.churn_rate != null;
  const churnPercent = churnAvailable ? summaryQ.data.churn_rate * 100 : null;

  return (
    <div data-testid={COCKPIT.page} className="space-y-6 max-w-7xl">
      {!isOnline && <OfflineBanner lastUpdatedLabel={lastUpdated ? lastUpdated.toLocaleTimeString() : "unknown"} />}

      <motion.div {...fadeUp(reducedMotion)} className="flex items-end justify-between flex-wrap gap-4">
        <div>
          <h1 className="text-3xl font-bold" style={{ color: "var(--scarlet-text)", letterSpacing: "-0.02em" }} data-testid={COCKPIT.greeting}>
            {timeAwareGreeting()}, {me?.name?.split(" ")[0] || "there"}
          </h1>
          <p className="text-base mt-1" style={{ color: "var(--scarlet-text-secondary)" }}>Here's how your gym is doing today.</p>
        </div>
        <div className="flex items-center gap-2">
          <select
            data-testid={COCKPIT.dateRangeSelect}
            value={range}
            onChange={(e) => setRange(e.target.value)}
            className="scarlet-focusable text-sm font-medium rounded-[10px] px-3 py-2 outline-none"
            style={{ border: "1px solid var(--scarlet-hairline)", color: "var(--scarlet-text)" }}
          >
            {Object.entries(RANGE_LABELS).map(([k, v]) => <option key={k} value={k}>{v}</option>)}
          </select>
        </div>
      </motion.div>

      {isEmpty ? (
        <EmptyCockpitState />
      ) : (
        <>
          <motion.div {...fadeUp(reducedMotion, 0.04)} className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
            {entitlements.expense !== false && (
              <StatCard
                testId={COCKPIT.kpiCard("revenue")}
                label={`Revenue · ${RANGE_LABELS[range]}`}
                value={pnlQ.data?.revenue ?? 0}
                formatValue={formatCurrency}
                subtitle={summaryQ.data ? `MRR: ${formatCurrency(summaryQ.data.mrr.amount)}` : undefined}
                loading={isLoading || pnlQ.isLoading}
                index={0}
                onClick={() => setDrilldown("revenue")}
              />
            )}
            <StatCard testId={COCKPIT.kpiCard("active-members")} label="Active members" value={summaryQ.data?.active_members ?? 0} loading={isLoading} index={1} onClick={() => setDrilldown("active-members")} />
            <StatCard testId={COCKPIT.kpiCard("checkins-today")} label="Check-ins today" value={summaryQ.data?.checkins_today ?? 0} loading={isLoading} index={2} onClick={() => setDrilldown("checkins-today")} />
            <StatCard testId={COCKPIT.kpiCard("dues-outstanding")} label="Dues outstanding" value={summaryQ.data?.dues_outstanding ?? 0} formatValue={formatCurrency} loading={isLoading} index={3} onClick={() => setDrilldown("dues-outstanding")} />
            {churnAvailable ? (
              <StatCard testId={COCKPIT.kpiCard("at-risk")} label="Churn rate" value={churnPercent} formatValue={(v) => `${v.toFixed(1)}%`} subtitle={`${summaryQ.data.at_risk_count} at-risk member(s)`} loading={isLoading} index={4} onClick={() => setDrilldown("at-risk")} />
            ) : (
              <StatCard testId={COCKPIT.kpiCard("at-risk")} label="At-risk members" value={summaryQ.data?.at_risk_count ?? 0} subtitle="Churn % needs more history to be reliable yet" loading={isLoading} index={4} onClick={() => setDrilldown("at-risk")} />
            )}
            <StatCard testId={COCKPIT.kpiCard("collections-today")} label="Collections today" value={summaryQ.data?.collections_today ?? 0} formatValue={formatCurrency} loading={isLoading} index={5} onClick={() => setDrilldown("collections-today")} />
            {adRoi?.spend > 0 && (
              <StatCard
                testId={COCKPIT.kpiCard("ad-roi")} label="Return on ad spend"
                value={adRoi.roi_multiple ?? 0}
                formatValue={(v) => `${v}×`}
                subtitle={`${formatCurrency(adRoi.revenue)} back on ${formatCurrency(adRoi.spend)}`}
                loading={marketingQ.isLoading} index={6}
                onClick={() => setDrilldown("ad-roi")}
              />
            )}
          </motion.div>

          {occupancy?.available !== false && (
            <motion.div {...fadeUp(reducedMotion, 0.08)} className="solid-surface rounded-[14px] p-5" data-testid={COCKPIT.occupancyWidget}>
              <OccupancyRing count={occupancy?.count ?? 0} capacity={occupancy?.capacity ?? 1} band={occupancy?.band} testId="occupancy-ring" />
            </motion.div>
          )}

          <div className="grid grid-cols-1 lg:grid-cols-3 gap-5">
            <div className="lg:col-span-1">
              <NeedsAttentionPanel items={attentionQ.data?.items || []} />
            </div>
            <div className="lg:col-span-2 space-y-4">
              <QuickActions />
              <RecentMembersTable rows={tableRows} loading={isLoading} />
            </div>
          </div>
        </>
      )}

      {drilldown && (
        <DrillDown
          metric={drilldown}
          branchId={branchParam}
          period={dateRangeFor(range)}
          link={DRILLDOWN_LINKS[drilldown]
            ? { ...DRILLDOWN_LINKS[drilldown], onClick: () => navigate(DRILLDOWN_LINKS[drilldown].to) }
            : undefined}
          onClose={() => setDrilldown(null)}
        />
      )}
    </div>
  );
}
